import * as React from "react";
import {
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarExport,
    GridToolbarFilterButton,
} from "@mui/x-data-grid";
import { EventId, RaceId } from "../../../domain/event";
import { EventDetailRace } from "../../../hooks/event";
import {
    addRank,
    checkpointsFromRace,
    ComputedIgnoreDeps,
    computeIgnored,
    isPassNotIgnored,
    lapsRaceOrd,
} from "./lapComputation";
import { ValidIsoDateString } from "../../../domain/common";
import { array, monoid, nonEmptyArray, number, option, record } from "fp-ts";
import {
    ResultWithCategoryTitle,
    useResultsWithCategoryTitle,
} from "../../../hooks/results";
import { flow, pipe } from "fp-ts/function";
import {
    isCompletedState,
    queryStateData,
} from "../../../utils/supabase/QueryState";
import { emptyArray, pick, rightOrThrow, someOrThrow } from "../../../utils/fp";
import { useAlertOnQueryError } from "../../../hooks/useAlertOnQueryError";
import {
    GridColumns,
    GridEnrichedColDef,
} from "@mui/x-data-grid/models/colDef/gridColDef";
import {
    GridValueFormatterParams,
    GridValueGetterParams,
} from "@mui/x-data-grid/models/params/gridCellParams";
import {
    bibPassAfterStartView,
    StoredTimeEntry,
    storedTimeEntryCodec,
    timeEntryFromStored,
    timeEntryFromTimeStringCodec,
    timeEntryToString,
} from "../../../domain/timing";
import { DataTable } from "../../DataTable";
import { Option } from "fp-ts/Option";
import * as t from "io-ts";
import { useQuery } from "../../../utils/supabase/useQuery";
import { column, columns } from "../../../utils/pg-ts/pg-ts";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import {
    ClubScoring,
    ClubScoringMember,
    computeClubStandings,
} from "../../../domain/race";
import { assertKey, Nullable } from "../../../utils/types";
import {
    isResultClassified,
    raceNumberOrd,
    unfinishedReasonLabels,
} from "../../../domain/result";
import { useTranslation } from "../../../utils/i18n/lang";
import { assertUnreachable } from "../../../utils/assertUnreachable";
import { LiveResultsIndividualsSettings } from "./LiveResultsIndividualsSettings";
export interface LiveResultsProps {
    eventId: EventId;
    race: EventDetailRace;
    gunshot: ValidIsoDateString;
    categoryFilter: Array<string>;
}

interface LiveResultsIndividualsProps extends LiveResultsProps {
    includeResultsWithoutBibPass?: boolean;
    onIncludeResultsWithoutBibPassChange?: (checked: boolean) => void;
}

export const LiveResultsIndividuals = (p: LiveResultsIndividualsProps) => {
    const individualsRows = useIndividualsRows(p, {
        includeResultsWithoutBibPass: p.includeResultsWithoutBibPass,
    });
    const Toolbar = React.useMemo(() => {
        const LiveResultsToolbar = () => (
            <GridToolbarContainer sx={{ gap: (theme) => theme.spacing(1) }}>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport
                    printOptions={{ disableToolbarButton: true }}
                    csvOptions={{
                        delimiter: ";",
                        utf8WithBom: true,
                    }}
                />
                {p.onIncludeResultsWithoutBibPassChange == null ? null : (
                    <LiveResultsIndividualsSettings
                        includeResultsWithoutBibPass={
                            p.includeResultsWithoutBibPass ?? false
                        }
                        onIncludeResultsWithoutBibPassChange={
                            p.onIncludeResultsWithoutBibPassChange
                        }
                    />
                )}
            </GridToolbarContainer>
        );

        return LiveResultsToolbar;
    }, [
        p.includeResultsWithoutBibPass,
        p.onIncludeResultsWithoutBibPassChange,
    ]);

    const unfinishedT = useTranslation(unfinishedReasonLabels);

    const tableCols = React.useMemo(() => {
        const cols: Array<Nullable<GridEnrichedColDef<TableRowIndividual>>> = [
            {
                field: "rank",
                headerName: "Pořadí",
                maxWidth: 50,
            },
            {
                field: "name",
                headerName: "Jméno",
                flex: 0.5,
                maxWidth: 200,
                minWidth: 150,
                valueGetter: (
                    params: GridValueGetterParams<any, TableRowIndividual>
                ) => params.row.result.name,
            },
            {
                field: "sex",
                headerName: "Pohlaví",
                valueGetter: (
                    params: GridValueGetterParams<any, TableRowIndividual>
                ) => params.row.result.sex,
            },
            {
                field: "born",
                headerName: "Ročník",
                maxWidth: 75,
                valueGetter: (
                    params: GridValueGetterParams<any, TableRowIndividual>
                ) => params.row.result.born,
            },
            {
                field: "number",
                headerName: "SČ",
                maxWidth: 75,
                valueGetter: (
                    params: GridValueGetterParams<any, TableRowIndividual>
                ) => params.row.result.number,
                sortComparator: raceNumberOrd.compare,
            },
            {
                field: "club",
                headerName: "Tým",
                maxWidth: 200,
                flex: 0.5,
                valueGetter: (
                    params: GridValueGetterParams<any, TableRowIndividual>
                ) => params.row.result.club_title,
            },
            {
                field: "bib",
                headerName: "Čip",
            },
            {
                field: "category",
                flex: 0.5,
                sortable: false,
                headerName: "Kategorie",
                filterable: false,
                valueGetter: (
                    params: GridValueGetterParams<any, TableRowIndividual>
                ) => params.row.result.category_title,
            },
            pipe(p.race.kind, (kind) => {
                switch (kind) {
                    case "duration":
                        return {
                            field: "laps",
                            headerName: "Kol",
                            type: "number",
                            maxWidth: 75,
                        };

                    case "distance":
                        return null;
                }
                assertUnreachable(kind);
            }),
            {
                field: "checkpointSum",
                headerName: "Čas",
                valueGetter: (
                    params: GridValueGetterParams<any, TableRowIndividual>
                ) =>
                    params.row.checkpointTimes.length === 0 &&
                    params.row.result.time == null
                        ? null
                        : params.row.checkpointSum,
                valueFormatter: (
                    params: GridValueFormatterParams<
                        Nullable<TableRowIndividual["checkpointSum"]>
                    >
                ) =>
                    pipe(
                        params.value,
                        option.fromNullable,
                        option.fold(
                            () => "",
                            flow(timeEntryFromStored, timeEntryToString)
                        )
                    ),
            },
            {
                field: "unfinishedReason",
                headerName: "Neklasifikace",
                maxWidth: 110,
                valueGetter: (
                    params: GridValueGetterParams<any, TableRowIndividual>
                ) =>
                    params.row.result.unfinished_reason == null
                        ? ""
                        : unfinishedT[params.row.result.unfinished_reason],
            },
            ...array.makeBy(
                individualsRows.checkpointsCount > 1
                    ? individualsRows.checkpointsCount
                    : 0,
                (i) => ({
                    field: `checkpoint_${i}`,
                    headerName: `${i + 1}. kolo`,
                    flex: 0.5,
                    maxWidth: 100,
                    minWidth: 100,
                    valueGetter: (
                        params: GridValueGetterParams<any, TableRowIndividual>
                    ) =>
                        params.row.checkpointTimes[i] -
                        pipe(
                            params.row.checkpointTimes[i - 1],
                            option.fromNullable,
                            option.getOrElse(() => 0)
                        ),
                    valueFormatter: (
                        params: GridValueFormatterParams<StoredTimeEntry>
                    ) =>
                        pipe(
                            params.value,
                            storedTimeEntryCodec.decode,
                            option.fromEither,
                            option.fold(
                                () => "",
                                flow(timeEntryFromStored, timeEntryToString)
                            )
                        ),
                })
            ),
        ];

        return pipe(cols, array.filterMap(option.fromNullable));
    }, [individualsRows.checkpointsCount, p.race.kind, unfinishedT]);

    return (
        <DataTable
            loading={individualsRows.loading}
            columns={tableCols}
            rows={individualsRows.rows}
            components={{ Toolbar }}
            initialState={{
                columns: {
                    columnVisibilityModel: {
                        bib: false,
                        born: false,
                        sex: false,
                        unfinishedReason: false,
                    },
                },
            }}
        />
    );
};

interface BibPassWithGunshotRelativeTimeEntry extends BibPass {
    startRelativeTimeEntry: StoredTimeEntry;
}

interface TableRowIndividual {
    id: string;
    bib: string;
    result: ResultWithCategoryTitle;
    rank: Nullable<number>;
    checkpointTimes: Array<StoredTimeEntry>;
    laps: number;
    checkpointSum: StoredTimeEntry;
}
type TableRowIndividualWithoutRank = Omit<TableRowIndividual, "rank">;

const isRankable = (row: TableRowIndividualWithoutRank) =>
    isResultClassified(row.result);

const bibPassColumns = pipe(
    bibPassAfterStartView.columns,
    pick("time", "bib", "id", "ignored")
);
const bibPassCodec = t.type(bibPassColumns, "BibPass");
type BibPass = t.TypeOf<typeof bibPassCodec>;
const useBibPassAfterStart = (raceId: RaceId) => {
    return useQuery(
        `bib_pass_after_start${raceId}}`,
        (supabase) =>
            supabase
                .from(bibPassAfterStartView.name)
                .select(columns(bibPassColumns))
                .eq(column(bibPassAfterStartView, "race_id"), raceId),
        t.array(bibPassCodec),
        {
            refreshInterval: 2000,
        }
    );
};

interface TableRowIndividualKnown
    extends Omit<TableRowIndividual, "rank">,
        ClubScoringMember {}
interface TableRowClub {
    id: string;
    rank: number;
    club: string;
    sum: number;
    individuals: NonEmptyArray<TableRowIndividualKnown>;
}
interface ClubProps extends LiveResultsProps {
    clubScoring: ClubScoring;
}
export const LiveResultsClubs = (p: ClubProps) => {
    const individualRows = useIndividualsRows(p);

    const tableCols = React.useMemo((): GridColumns<TableRowClub> => {
        const sumCol: GridEnrichedColDef<TableRowClub> = pipe(
            p.clubScoring.scoringMethod,
            (scoringMethod) => {
                switch (scoringMethod) {
                    case "timeSum":
                        return {
                            field: assertKey<TableRowClub>("sum"),
                            headerName: "Čas",
                            valueFormatter: (
                                params: GridValueFormatterParams<
                                    TableRowClub["sum"]
                                >
                            ) =>
                                pipe(
                                    params.value,
                                    storedTimeEntryCodec.decode,
                                    option.fromEither,
                                    option.fold(
                                        () => String(params.value),
                                        flow(
                                            timeEntryFromStored,
                                            timeEntryToString
                                        )
                                    )
                                ),
                        };

                    case "rankSum":
                        return {
                            field: assertKey<TableRowClub>("sum"),
                            headerName: "Soušet umístění",
                            type: "number",
                        };
                }

                assertUnreachable(scoringMethod);
            }
        );

        return [
            {
                field: assertKey<TableRowClub>("rank"),
                headerName: "Pořadí",
                maxWidth: 50,
            },
            {
                field: assertKey<TableRowClub>("club"),
                headerName: "Tým",
                flex: 0.5,
            },
            {
                field: "finished",
                headerName: "Členů v cíli",
                flex: 0.5,
                valueGetter: (
                    params: GridValueGetterParams<any, TableRowClub>
                ) => params.row.individuals.length,
            },
            sumCol,
        ];
    }, [p.clubScoring]);

    const rows = React.useMemo(
        (): Array<TableRowClub> =>
            pipe(
                individualRows.rows,
                array.filterMap(
                    (row): Option<TableRowIndividualKnown> =>
                        row.result.club_title == null || row.rank == null
                            ? option.none
                            : option.some({
                                  ...row,
                                  rank: row.rank,
                                  clubTitle: row.result.club_title,
                                  time: row.checkpointSum,
                              })
                ),
                computeClubStandings(p.clubScoring),
                array.map((standing) => ({
                    id: standing.club,
                    rank: standing.rank,
                    club: standing.club,
                    sum: standing.sum,
                    individuals: standing.members,
                }))
            ),
        [individualRows.rows, p.clubScoring]
    );

    return (
        <DataTable
            loading={individualRows.loading}
            columns={tableCols}
            rows={rows}
        />
    );
};

interface UseIndividualsRowsOptions {
    includeResultsWithoutBibPass?: boolean;
}

const useIndividualsRows = (
    p: LiveResultsProps,
    options?: UseIndividualsRowsOptions
) => {
    const includeResultsWithoutBibPass =
        options?.includeResultsWithoutBibPass ?? false;

    const resultsQuery = useResultsWithCategoryTitle([p.race.id], 10000);
    const results = React.useMemo(
        () =>
            pipe(
                resultsQuery.state,
                queryStateData,
                option.getOrElse(emptyArray())
            ),
        [resultsQuery.state]
    );
    useAlertOnQueryError(resultsQuery.state);

    const bibPassQuery = useBibPassAfterStart(p.race.id);
    useAlertOnQueryError(bibPassQuery.state);

    React.useEffect(() => {
        bibPassQuery.mutate();
    }, [results]);

    const checkpoints = pipe(p.race, checkpointsFromRace, someOrThrow);

    const bibPassByBib = React.useMemo(
        () =>
            pipe(
                bibPassQuery.state,
                queryStateData,
                option.getOrElse(emptyArray()),
                nonEmptyArray.groupBy((pass) => pass.bib)
            ),
        [bibPassQuery.state]
    );

    const rows = React.useMemo(() => {
        const gunshotTimeEntry = pipe(
            p.gunshot,
            (s) => s.split("T")[1],
            timeEntryFromTimeStringCodec.decode,
            rightOrThrow
        );

        return pipe(
            results,
            array.filterMap((result) => {
                const bibPassList = pipe(
                    result.bib,
                    option.fromNullable,
                    option.chain((bib) =>
                        pipe(bibPassByBib, record.lookup(bib))
                    )
                );

                const startTimeEntry = pipe(
                    result.start_time,
                    option.fromNullable,
                    option.fold(
                        () => gunshotTimeEntry,
                        (startTime) => gunshotTimeEntry + startTime
                    ),
                    (startTime) => startTime as StoredTimeEntry
                );

                return pipe(
                    bibPassList,
                    option.map((passList): TableRowIndividualWithoutRank => {
                        const checkpointTimes = pipe(
                            passList,
                            computeCheckpointTimes(startTimeEntry, {
                                checkpointList: checkpoints,
                                fluidLapsCount: p.race.kind === "duration",
                            })
                        );

                        return {
                            id: String(result.id),
                            bib: result.bib ?? "",
                            result,
                            checkpointTimes,
                            checkpointSum: pipe(
                                checkpointTimes,
                                array.last,
                                option.getOrElse(() => 0 as StoredTimeEntry)
                            ),
                            laps: checkpointTimes.length,
                        };
                    }),
                    option.alt(() =>
                        includeResultsWithoutBibPass
                            ? option.some<TableRowIndividualWithoutRank>({
                                  id: String(result.id),
                                  bib: result.bib ?? "",
                                  result,
                                  checkpointTimes:
                                      emptyArray<StoredTimeEntry>()(),
                                  checkpointSum:
                                      result.time ?? (0 as StoredTimeEntry),
                                  laps:
                                      p.race.kind === "duration"
                                          ? result.laps_completed ?? 0
                                          : result.time == null
                                          ? 0
                                          : checkpoints.length,
                              })
                            : option.none
                    )
                );
            }),
            array.filter(
                (row) =>
                    p.categoryFilter.length === 0 ||
                    (row.result.category_title != null &&
                        p.categoryFilter.includes(row.result.category_title))
            ),
            (filtered) => {
                const ranked = pipe(
                    filtered,
                    array.filter(isRankable),
                    nonEmptyArray.fromArray,
                    option.map(
                        addRank<TableRowIndividualWithoutRank>(lapsRaceOrd)
                    ),
                    option.getOrElse(
                        emptyArray<
                            TableRowIndividualWithoutRank & { rank: number }
                        >()
                    )
                );

                const unranked: Array<TableRowIndividual> = pipe(
                    filtered,
                    array.filter((row) => !isRankable(row)),
                    array.map((row) => ({ ...row, rank: null }))
                );

                return [...ranked, ...unranked];
            }
        );
    }, [
        bibPassByBib,
        checkpoints,
        includeResultsWithoutBibPass,
        p.categoryFilter,
        p.gunshot,
        p.race.kind,
        results,
    ]);

    return {
        rows,
        checkpointsCount: pipe(
            rows,
            array.foldMap(monoid.max(number.Bounded))(
                (row: TableRowIndividual) => row.checkpointTimes.length
            )
        ),
        loading: !isCompletedState(resultsQuery.state),
    };
};

const computeCheckpointTimes =
    (startTimeEntry: StoredTimeEntry, assignPassDeps: ComputedIgnoreDeps) =>
    (bibPassGroup: NonEmptyArray<BibPass>) =>
        pipe(
            bibPassGroup,
            nonEmptyArray.map(addRelativeTimeEntry(startTimeEntry)),
            computeIgnored(assignPassDeps),
            array.filter(isPassNotIgnored),
            array.map((pass) => pass.startRelativeTimeEntry)
        );

const addRelativeTimeEntry =
    (startTimeEntry: StoredTimeEntry) =>
    (pass: BibPass): BibPassWithGunshotRelativeTimeEntry => ({
        ...pass,
        startRelativeTimeEntry: pipe(
            pass.time,
            timeEntryFromTimeStringCodec.decode,
            rightOrThrow, // TODO? one wrong item kill whole result list
            (timeEntry) => timeEntry - startTimeEntry, // calculus
            storedTimeEntryCodec.decode,
            rightOrThrow // TODO? one wrong item kill whole result list
        ),
    });
